// Session Security Utilities
// This file contains utilities to ensure proper session management and security

export interface SessionSecurityConfig {
    enableStrictMode: boolean;
    maxSessionAge: number; // in milliseconds
    requireRevalidation: boolean;
}

const DEFAULT_CONFIG: SessionSecurityConfig = {
    enableStrictMode: true,
    maxSessionAge: 24 * 60 * 60 * 1000, // 24 hours
    requireRevalidation: true
};

// Check if current session is valid
export const validateCurrentSession = (config: Partial<SessionSecurityConfig> = {}): boolean => {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    
    try {
        const token = sessionStorage.getItem('token');
        const user = sessionStorage.getItem('user');
        const sessionId = sessionStorage.getItem('sessionId');
        
        // Basic validation
        if (!token || !user || !sessionId) {
            // console.log('🔒 Session validation failed: Missing required data');
            return false;
        }
        
        // Parse and validate token
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
            // console.log('🔒 Session validation failed: Invalid token format');
            return false;
        }
        
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Date.now() / 1000;
        
        // Check token expiration
        if (payload.exp && payload.exp < currentTime) {
            // console.log('🔒 Session validation failed: Token expired');
            return false;
        }
        
        // Check session age if strict mode is enabled
        if (finalConfig.enableStrictMode && payload.iat) {
            const sessionAge = (currentTime - payload.iat) * 1000;
            if (sessionAge > finalConfig.maxSessionAge) {
                // console.log('🔒 Session validation failed: Session too old');
                return false;
            }
        }
        
        // Validate user data
        const userData = JSON.parse(user);
        if (!userData.id || userData.id !== payload.id) {
            // console.log('🔒 Session validation failed: User data mismatch');
            return false;
        }
        
        // console.log('✅ Session validation passed');
        return true;
        
    } catch (error) {
        console.error('🔒 Session validation error:', error);
        return false;
    }
};

// Force session cleanup
export const forceSessionCleanup = (): void => {
    console.log('🧹 Performing forced session cleanup');
    
    // Clear all session data
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('sessionStartTime');
    sessionStorage.removeItem('lastKnownSession');
    
    // Clear any legacy localStorage data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionId');
    
    console.log('✅ Session cleanup completed');
};

// Check if this is a new browser session
export const isNewBrowserSession = (): boolean => {
    const sessionStartTime = sessionStorage.getItem('sessionStartTime');
    return !sessionStartTime;
};

// Get session information for debugging
export const getSessionInfo = () => {
    // Check if this is a temporary access scenario (different from regular sessions)
    const isTempAccess = window.location.search.includes('temp_access=true');
    const tempToken = new URLSearchParams(window.location.search).get('token');
    
    // Get actual values for debugging
    const actualToken = sessionStorage.getItem('token');
    const actualUser = sessionStorage.getItem('user');
    const actualSessionId = sessionStorage.getItem('sessionId');
    
    // console.log('🔍 getSessionInfo called:', {
    //     isTempAccess,
    //     hasTempToken: !!tempToken,
    //     tempTokenLength: tempToken ? tempToken.length : 0,
    //     currentUrl: window.location.href,
    //     currentPath: window.location.pathname,
    //     searchParams: window.location.search
    // });
    
    // console.log('🔍 SessionStorage contents:', {
    //     hasToken: !!actualToken,
    //     tokenLength: actualToken ? actualToken.length : 0,
    //     hasUser: !!actualUser,
    //     userLength: actualUser ? actualUser.length : 0,
    //     hasSessionId: !!actualSessionId,
    //     sessionIdLength: actualSessionId ? actualSessionId.length : 0
    // });
    
    return {
        hasToken: !!actualToken || !!tempToken,
        hasUser: !!actualUser,
        hasSessionId: !!actualSessionId,
        sessionStartTime: sessionStorage.getItem('sessionStartTime'),
        lastKnownSession: sessionStorage.getItem('lastKnownSession'),
        isNewSession: isNewBrowserSession(),
        isTempAccess,
        tempToken: !!tempToken,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
    };
};

// Security check for session hijacking prevention
export const performSecurityCheck = (): boolean => {
    const sessionInfo = getSessionInfo();
    
    // console.log('🔍 Performing security check:', {
    //     path: window.location.pathname,
    //     hasToken: sessionInfo.hasToken,
    //     hasUser: sessionInfo.hasUser,
    //     hasSessionId: sessionInfo.hasSessionId,
    //     tempAccess: window.location.search.includes('temp_access=true')
    // });
    
    // Skip security checks on login/authentication pages
    if (window.location.pathname.includes('/login') || 
        window.location.pathname.includes('/signup') || 
        window.location.pathname.includes('/password-reset')) {
        console.log('✅ Login/auth page detected - skipping security check');
        return true;
    }
    
    // Skip security checks on logout or when no session exists
    if (!sessionInfo.hasToken && !sessionInfo.hasUser) {
        console.log('✅ No session data - skipping security check');
        return true;
    }
    
    // Only perform basic validation - don't be overly aggressive
    if (sessionInfo.hasToken && sessionInfo.hasUser) {
        console.log('✅ Valid session data detected - security check passed');
        return true;
    }
    
    // If we have user data but no token, that's suspicious
    if (!sessionInfo.hasToken && sessionInfo.hasUser) {
        console.warn('🚨 Suspicious pattern: user data without token');
        return false;
    }
    
    console.log('✅ Security check passed');
    return true;
};

// Initialize session security
export const initializeSessionSecurity = (): void => {
    console.log('🔒 Initializing session security for path:', window.location.pathname);
    
    // Skip session security on public/auth pages
    if (window.location.pathname.includes('/login') || 
        window.location.pathname.includes('/signup') || 
        window.location.pathname.includes('/password-reset') ||
        window.location.pathname === '/' ||
        window.location.pathname === '/health') {
        console.log('🔒 Public/auth page detected - skipping session security');
        return;
    }
    
    // Check if this is a temporary access scenario
    if (window.location.search.includes('temp_access=true')) {
        console.log('🔒 Temporary access detected - being more lenient with session security');
    }
    
    // Set up session start time if not exists
    if (isNewBrowserSession()) {
        sessionStorage.setItem('sessionStartTime', Date.now().toString());
        console.log('🆕 New browser session detected');
    }
    
    console.log('✅ Session security initialized successfully');
};

// Export default configuration
export { DEFAULT_CONFIG };